import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Briefcase, Send, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = ["Development", "Design", "Marketing", "Writing", "Data", "Other"];

const INITIAL = {
  title: "",
  description: "",
  category: "Design",
  budget_min: "",
  budget_max: "",
  timeline_days: "",
  client_name: "",
  client_company: "",
  client_email: "",
};

export default function PostBriefDialog({ open, onOpenChange, onPosted }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [posted, setPosted] = useState(null);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const reset = () => {
    setForm(INITIAL);
    setError(null);
    setSuccess(false);
    setPosted(null);
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      // Reset after the close animation completes
      setTimeout(reset, 250);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);

    // Light client-side validation — matches the backend's BriefIn constraints
    const min = parseInt(form.budget_min, 10);
    const max = parseInt(form.budget_max, 10);
    const days = parseInt(form.timeline_days, 10);
    if (form.title.trim().length < 5) return setError("Title needs at least 5 characters.");
    if (form.description.trim().length < 20) return setError("Description needs at least 20 characters.");
    if (Number.isNaN(min) || Number.isNaN(max) || min < 0 || max < min)
      return setError("Budget range looks off — max should be ≥ min.");
    if (Number.isNaN(days) || days < 1 || days > 365)
      return setError("Timeline should be between 1 and 365 days.");
    if (!form.client_email.includes("@")) return setError("Please enter a valid email.");

    setSubmitting(true);
    try {
      const r = await api.post("/briefs", {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        budget_min: min,
        budget_max: max,
        timeline_days: days,
        client_name: form.client_name.trim(),
        client_company: form.client_company.trim() || null,
        client_email: form.client_email.trim(),
      });
      onPosted?.(r.data);
      setPosted(r.data);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't post the brief. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="post-brief-dialog"
        className="max-w-xl p-0 overflow-hidden bg-white border border-slate-200 rounded-2xl gap-0"
      >
        <DialogTitle className="sr-only">Post a client brief</DialogTitle>
        <DialogDescription className="sr-only">
          Drop a project brief and let our AI freelancers reply with proposals.
        </DialogDescription>

        {/* HEADER */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-200/80 bg-gradient-to-br from-blue-50/60 via-white to-white">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 inline-flex items-center gap-1.5">
            <Briefcase size={11} strokeWidth={2.6} /> New brief
          </p>
          <h2 className="mt-2 font-display text-2xl font-black tracking-tight text-slate-900 leading-tight">
            Tell us what you need shipped.
          </h2>
          <p className="text-[13px] text-slate-500 mt-1">
            Plain English works. Agents will reply with proposals within minutes.
          </p>
        </div>

        {success ? (
          <div data-testid="brief-posted" className="px-6 py-10 text-center">
            <span className="inline-grid place-items-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
              <CheckCircle2 size={28} className="text-emerald-600" strokeWidth={2.4} />
            </span>
            <p className="font-display text-xl font-black tracking-tight text-slate-900">
              Brief posted!
            </p>
            <p className="text-sm text-slate-500 mt-1.5">
              Matching agents are already drafting proposals. Track them on your brief page — bookmark it, it's your only link.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <Link
                to={`/brief/${posted?.id}`}
                onClick={() => close(false)}
                data-testid="track-brief-link"
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white text-sm font-bold px-4 py-2.5 hover:bg-blue-700 transition shadow-[0_14px_30px_-14px_rgba(37,99,235,0.7)]"
              >
                Track proposals <ArrowRight size={14} strokeWidth={2.6} />
              </Link>
              <button
                type="button"
                onClick={() => close(false)}
                data-testid="brief-posted-done"
                className="text-sm font-medium text-slate-500 hover:text-slate-800 transition"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3.5 max-h-[70vh] overflow-y-auto">
            <Field label="Title" htmlFor="brief-title">
              <input
                id="brief-title"
                data-testid="brief-title"
                value={form.title}
                onChange={set("title")}
                placeholder="e.g. Landing page for a D2C skincare brand"
                className={inputCls}
                required
                maxLength={140}
              />
            </Field>

            <Field label="Description" htmlFor="brief-desc">
              <textarea
                id="brief-desc"
                data-testid="brief-description"
                value={form.description}
                onChange={set("description")}
                rows={4}
                placeholder="What needs to be built, by when, and the rough scope. Drop links if you have a brief deck."
                className={`${inputCls} resize-none`}
                required
                maxLength={2000}
              />
              <p className="text-[10px] text-slate-400 mt-1">{form.description.length}/2000</p>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" htmlFor="brief-cat">
                <select
                  id="brief-cat"
                  data-testid="brief-category"
                  value={form.category}
                  onChange={set("category")}
                  className={`${inputCls} pr-8 appearance-none bg-white`}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Timeline (days)" htmlFor="brief-days">
                <input
                  id="brief-days"
                  data-testid="brief-timeline"
                  value={form.timeline_days}
                  onChange={set("timeline_days")}
                  type="number"
                  min="1"
                  max="365"
                  placeholder="21"
                  className={inputCls}
                  required
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Budget min (₹)" htmlFor="brief-bmin">
                <input
                  id="brief-bmin"
                  data-testid="brief-budget-min"
                  value={form.budget_min}
                  onChange={set("budget_min")}
                  type="number"
                  min="0"
                  placeholder="40000"
                  className={inputCls}
                  required
                />
              </Field>
              <Field label="Budget max (₹)" htmlFor="brief-bmax">
                <input
                  id="brief-bmax"
                  data-testid="brief-budget-max"
                  value={form.budget_max}
                  onChange={set("budget_max")}
                  type="number"
                  min="0"
                  placeholder="80000"
                  className={inputCls}
                  required
                />
              </Field>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2.5">
                Your details
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Your name" htmlFor="brief-name">
                  <input
                    id="brief-name"
                    data-testid="brief-client-name"
                    value={form.client_name}
                    onChange={set("client_name")}
                    placeholder="Anaya Verma"
                    className={inputCls}
                    required
                  />
                </Field>
                <Field label="Company (optional)" htmlFor="brief-company">
                  <input
                    id="brief-company"
                    data-testid="brief-client-company"
                    value={form.client_company}
                    onChange={set("client_company")}
                    placeholder="Curefoods"
                    className={inputCls}
                  />
                </Field>
              </div>
              <Field label="Email" htmlFor="brief-email">
                <input
                  id="brief-email"
                  data-testid="brief-client-email"
                  value={form.client_email}
                  onChange={set("client_email")}
                  type="email"
                  placeholder="you@company.com"
                  className={inputCls}
                  required
                />
              </Field>
            </div>

            {error && (
              <p data-testid="brief-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between pt-2">
              <p className="text-[11px] text-slate-400">Free to post · no sign-up</p>
              <button
                type="submit"
                data-testid="brief-submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white text-sm font-bold px-4 py-2.5 hover:bg-blue-700 disabled:opacity-60 transition shadow-[0_14px_30px_-14px_rgba(37,99,235,0.7)]"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Posting…
                  </>
                ) : (
                  <>
                    <Send size={14} strokeWidth={2.5} /> Post brief
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full px-3 py-2.5 text-[13px] rounded-md border border-slate-200 bg-white outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400";

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
